import React, { ElementType, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Info, Settings } from 'lucide-react';
import Composer from '../components/Composer';
import {
  deleteFacebookConversation,
  getFacebookConversation,
  getFacebookMessages,
  getFacebookProfile,
} from '../../../hooks/facebook';
import { FacebookConversation, FacebookMessage } from '../../../utils/types';
import DeleteChatButton from '../components/DeleteConversation';
import {
  FacebookBubble,
  FacebookDateSeparator,
  fbBorder,
    formatMessengerDay,
    formatMessengerTime,
    getFacebookMessageAttachments,
    getFacebookMessageStatus,
  } from '../components/facebookMessengerUi';

const statusLabel = (message: FacebookMessage) => {
  const status = getFacebookMessageStatus(message);
  if (!status) return '';
  if (status === 'failed') return `Failed • ${formatMessengerTime(message.timestamp || message.created_at)}`;
  return `${status.charAt(0).toUpperCase()}${status.slice(1)} • ${formatMessengerTime(message.timestamp || message.created_at)}`;
};

export default function FacebookConversationDetailSmall() {
  const { orgId, convId } = useParams();
  const [messages, setMessages] = useState<FacebookMessage[]>([]);
  const [conversation, setConversation] = useState<FacebookConversation>();
  const [facebookProfileId, setFacebookProfileId] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const fetchConversation = async () => {
    if (!convId || !orgId) return;
    try {
      setLoading(true);
      const [conversationResponse, messagesResponse] = await Promise.all([
        getFacebookConversation(orgId, convId),
        getFacebookMessages(convId, orgId),
      ]);

      if (conversationResponse.success) {
        setConversation(conversationResponse.data);
      }

      if (messagesResponse.success) {
        const normalized = Array.isArray(messagesResponse.data)
          ? messagesResponse.data
          : messagesResponse.data?.results || [];
        setMessages(normalized);
      }
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
    } finally {
      setLoading(false);
      setTimeout(() => scrollToBottom('auto'), 50);
    }
  };

  useEffect(() => {
    fetchConversation();
  }, [convId, orgId]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!orgId) return;
      const response = await getFacebookProfile(orgId);
      if (response.success) {
        if (Array.isArray(response.data) && response.data[0]?.id) {
          setFacebookProfileId(response.data[0].id);
        } else if (response.data?.id) {
          setFacebookProfileId(response.data.id);
        }
      }
    };

    fetchProfile();
  }, [orgId]);

  useEffect(() => {
    if (!convId || !orgId) return;
    const interval = window.setInterval(() => {
      fetchConversation();
    }, 8000);

    return () => window.clearInterval(interval);
  }, [convId, orgId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  const handleSendMessage = async (msg: FacebookMessage) => {
    setMessages((prev) => [...prev, msg]);
  };

  const handleDeleteConversation = async () => {
    if (!orgId || !convId) return;
    const response = await deleteFacebookConversation(orgId, convId);
    if (response.success) {
      navigate(`/organization/${orgId}/inbox/facebook/conversations`);
    }
  };

  const threadItems = useMemo(() => {
    let lastDay = '';
    return messages.flatMap((message) => {
      const messageDate = message.timestamp || message.created_at;
      const label = formatMessengerDay(messageDate);
      const items: Array<{ type: 'date' | 'message'; value: string | FacebookMessage }> = [];

      if (label && label !== lastDay) {
        lastDay = label;
        items.push({ type: 'date', value: label });
      }

      items.push({ type: 'message', value: message });
      return items;
    });
  }, [messages]);

  return (
    <Flex direction="column" h="100vh" bg="#eef3fb">
      <Flex
        align="center"
        justify="space-between"
        px={{ base: 3, md: 5 }}
        py={3}
        bg="rgba(255,255,255,0.92)"
        borderBottom="1px solid"
        borderColor={fbBorder}
        backdropFilter="blur(12px)"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <HStack spacing={3}>
          <IconButton
            aria-label="Back"
            icon={<Icon as={ChevronLeft as ElementType} boxSize={5} />}
            variant="ghost"
            borderRadius="12px"
            onClick={() => navigate(-1)}
            display={{ base: 'inline-flex', md: 'none' }}
          />
          <Avatar
            name={conversation?.sender_name || 'User'}
            src={conversation?.sender_profile_picture || undefined}
            bg="#1877F2"
            color="white"
            size="md"
          />
          <VStack align="flex-start" spacing={0}>
            <Text fontWeight="700" color="gray.900">
              {conversation?.sender_name || 'Facebook user'}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {conversation?.requires_utility_message
                ? 'Utility message required'
                : 'Free text available'}
            </Text>
          </VStack>
        </HStack>

        <HStack spacing={2}>
          <IconButton
            aria-label="Conversation info"
            icon={<Icon as={Info as ElementType} boxSize={4} />}
            variant="ghost"
            borderRadius="12px"
          />
          {facebookProfileId ? (
            <IconButton
              aria-label="Page settings"
              icon={<Icon as={Settings as ElementType} boxSize={4} />}
              variant="ghost"
              borderRadius="12px"
              onClick={() =>
                navigate(`/organization/${orgId}/inbox/facebook/${facebookProfileId}/settings`)
              }
            />
          ) : null}
          <DeleteChatButton onConfirm={handleDeleteConversation} />
        </HStack>
      </Flex>

      <Box flex="1" overflowY="auto" px={{ base: 3, md: 6 }} py={{ base: 3, md: 5 }}>
        {loading ? (
          <Flex justify="center" align="center" h="100%">
            <Spinner color="#1877F2" />
          </Flex>
        ) : messages.length === 0 ? (
          <Flex justify="center" align="center" h="100%">
            <VStack spacing={3} textAlign="center">
              <Text fontWeight="700" color="gray.800">
                No messages yet
              </Text>
              <Text fontSize="sm" color="gray.500" maxW="320px">
                Start the conversation from here once the customer messages your connected page or you send a policy-allowed response.
              </Text>
            </VStack>
          </Flex>
        ) : (
          <VStack spacing={0} align="stretch">
            {threadItems.map((item, index) =>
              item.type === 'date' ? (
                <FacebookDateSeparator key={`date-${item.value}-${index}`} label={item.value as string} />
              ) : (
                <Flex
                  key={(item.value as FacebookMessage).id}
                  justify={(item.value as FacebookMessage).direction === 'outgoing' ? 'flex-end' : 'flex-start'}
                  mb={3}
                >
                  <FacebookBubble
                    outgoing={(item.value as FacebookMessage).direction === 'outgoing'}
                    failed={getFacebookMessageStatus(item.value as FacebookMessage) === 'failed'}
                    meta={statusLabel(item.value as FacebookMessage)}
                    attachments={getFacebookMessageAttachments(item.value as FacebookMessage)}
                  >
                    {(item.value as FacebookMessage).text ||
                      (getFacebookMessageAttachments(item.value as FacebookMessage).length
                        ? 'Attachment'
                        : 'No content')}
                  </FacebookBubble>
                </Flex>
              ),
            )}
            <div ref={messagesEndRef} />
          </VStack>
        )}
      </Box>

      <Box
        bg="rgba(255,255,255,0.94)"
        borderTop="1px solid"
        borderColor={fbBorder}
        backdropFilter="blur(12px)"
        px={{ base: 3, md: 5 }}
        py={3}
        position="sticky"
        bottom={0}
      >
        {conversation && orgId && convId ? (
          <Composer
            requires_utility_message={conversation.requires_utility_message}
            conversationId={convId}
            orgId={orgId}
            profileId={conversation.facebook_page_id}
            onSent={handleSendMessage}
          />
        ) : null}
      </Box>
    </Flex>
  );
}
